import React, { Component, Suspense, lazy } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

const DatePicker = lazy(() => import("react-datepicker"));

function debounce(fn, delay) {
  let timer = null;
  return function() {
    let context = this,
      args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function() {
      fn.apply(context, args);
    }, delay);
  };
}

const InputWrapper = styled.label`
  direction: ltr !important;
  position: relative;
  display: block;
  min-height: 38px;
  max-height: 38px;
  @media (max-width: 480px) {
    min-height: 32px;
    max-height: 32px;
  }

  ${props =>
    props.desktop &&
    css`
      min-height: 39px;
      max-height: 39px;
    `} ${props =>
    props.currency &&
    css`
      display: table;
      width: 100%;
    `};
`;

export const InputView = styled.input`
  vertical-align: top;
  width: 100%;
  min-height: 38px;
  padding: ${props => props.type === "date" ? "0 29px 0 7px" : "0 7px"};
  margin: 0px;
  box-sizing: border-box;
  font-weight: 400;
  font-size: 16px;
  line-height: 1;
  font-family: Roboto, Arial, sans-serif;
  color: #4e2a81;
  background-color: #fff;
  border: 1px solid #cfcfcf;
  border-radius: 3px;
  box-shadow: inset 0px 2px 0px 0px rgba(227,227,227,1);
  :focus {
    outline: none;
  }
  @media (max-width: 480px) {
    min-height: 32px;
    font-size: ${({ theme }) =>
      theme.locale === "ar" ? theme.minFontSize : "14px"};
  }

  ${props =>
    props.desktop &&
    css`
      min-height: 39px;
      border-color: #cfcfcf;
      color: #4e2a81;
    `}

  ${props =>
    props.Icon &&
    props.type !== "date" &&
    props.type !== "time" &&
    css`
      padding-left: 30px;
    `}

  ${props =>
    props.currency &&
    css`
      display: table-cell;
      width: 100%;
      border-radius: 3px 0 0 3px;
      border-right: none;
    `}

  ${props =>
    props.invalid &&
    css`
      border-color: #e85747;
      color: #e74c3c;
    `}

  ${props =>
    props.disabled &&
    css`
      background-color: #fff;
      color: #4e2a81;
      pointer-events: none;
      user-select: none;
      cursor: default;
    `}

  ${props =>
    props.offlinePopup &&
    props.disabled &&
    css`
      background-color: #f6f6f6;
    `}

  ${props =>
    props.promo &&
    css`
      font-size: 16px;
      color: #1b546a;
      text-align: center;
      border-radius: 4px;
      border-color: #ccc;
      box-shadow: none;
    `};
`;

const Label = styled.span`
  display: ${props => (props.desktop ? "none" : "inline-block")};
  width: 100%;
  padding-bottom: 6px;
  font-weight: 400;
  font-size: 18px;
  line-height: 1;
  color: #797979;
  direction: ${props => props.theme.direction};
  @media (max-width: 480px) {
    font-size: 16px;
  }
  @media (max-width: 320px) {
    font-size: ${({ theme }) =>
      theme.locale === "ar" ? theme.minFontSize : "14px"};
  }
`;

const LabelIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 0px;
  transform: translateY(-50%);
  padding: 0 7px;
  line-height: 39px;
  z-index: 99;
  pointer-events: none;
  @media (max-width: 959px) {
    line-height: 38px;
    padding: 0 5px;
  }
  @media (max-width: 480px) {
    line-height: 32px;
  }

  ${props =>
    (props.type === "date") | (props.type === "time") &&
    css`
      left: 100%;
      transform: translate(-100%, -50%);
      margin-left: -1px;
    `};
`;

const LabelCurrency = styled.span`
  display: table-cell;
  vertical-align: middle;
  height: 39px;
  width: 1px;
  padding: 0 9px;
  box-sizing: border-box;
  font-weight: 400;
  line-height: 0;
  border-top: 1px solid #cfcfcf;
  border-right: 1px solid #cfcfcf;
  border-bottom: 1px solid #cfcfcf;
  border-left: none;
  border-radius: 0 3px 3px 0;
  box-shadow: inset 0px 2px 0px 0px rgba(227, 227, 227, 1);
  background-color: #f6f6f6;
  background-clip: padding-box;

  ${props =>
    !props.desktop &&
    css`
      height: auto;
      line-height: 1;
      font-size: 16px;
      white-space: nowrap;
      @media (max-width: 480px) {
        font-size: ${({ theme }) =>
          theme.locale === "ar" ? theme.minFontSize : "14px"};
      }
    `} ${props =>
    props.invalid &&
    css`
      border-top: 1px solid #e85747;
      border-right: 1px solid #e85747;
      border-bottom: 1px solid #e85747;
    `};
`;

export default class Input extends Component {
  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.focus = this.focus.bind(this);
  }

  onChangeDeb(value) {
    this.props.onChangeDeb(value);
  }

  onChange(e) {
    e.persist();

    const value = e.target.value;

    if (typeof this.props.onChange === "function") {
      this.props.onChange(value);
    } else if (typeof this.props.onChangeDeb === "function") {
      this.onChangeDeb(value);
    }
  }

  UNSAFE_componentWillMount() {
    this.onChangeDeb = debounce(this.onChangeDeb, 1000);
  }

  componentDidMount() {
    this.props.focus && this.input.focus();
  }

  focus() {
    this.input && this.input.focus();
  }

  render() {
    const {
      type = "text",
      id,
      label,
      defaultValue,
      value,
      placeholder,
      desktop,
      invalid,
      disabled,
      Icon,
      currency,
      onFocus,
      onBlur,
      offlinePopup,
      readOnly,
      onChange,
      maxDate,
      autoComplete,
      pattern,
      inputMode,
      promo
    } = this.props;

    let valueF = value;

    const isDesktopDate = type === "date" && desktop;
    const isMobileDate = type === "date" && !desktop;

    if (isMobileDate && value && typeof value.format === "function") {
      // Format moment.js object to string
      valueF = value.format("YYYY-MM-DD");
    }

    const isAndroid = /Android 4\.\d/.test(navigator.userAgent);
    const isWebview = /Version\/4\.\d/.test(navigator.userAgent);
    const isAndroidWebview = isAndroid && isWebview;
    const dateTimeType = type === "date" || type === "time";

    return (
      <div>
        {label && id && (
          <Label desktop={desktop} htmlFor={id}>
            {label}:
          </Label>
        )}
        <InputWrapper htmlFor={id} desktop={desktop} currency={currency}>
          {id && Icon && (
            <LabelIcon type={type}>
              <Icon />
            </LabelIcon>
          )}
          {isDesktopDate ? (
          <Suspense fallback={null}>
            <InputView
              as={DatePicker}
              type="date"
              selected={value}
              onChange={onChange}
              dateFormat="dd.MM.yyyy"
              desktop={desktop}
              Icon={Icon}
              invalid={invalid}
              offlinePopup={offlinePopup}
              disabled={disabled}
              maxDate={maxDate}
              autoComplete={autoComplete}
            />
          </Suspense>
          ) : (
            <InputView
              ref={input => (this.input = input)}
              type={isAndroidWebview && dateTimeType ? "text" : type}
              id={id}
              placeholder={placeholder}
              desktop={desktop}
              Icon={Icon}
              currency={currency}
              invalid={invalid}
              offlinePopup={offlinePopup}
              defaultValue={defaultValue}
              value={valueF}
              disabled={disabled}
              onChange={this.onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              readOnly={readOnly}
              autoComplete={autoComplete}
              pattern={pattern}
              inputMode={inputMode}
              promo={promo}
            />
          )}
          {id && currency && (
            <LabelCurrency desktop={desktop} invalid={invalid}>
              {currency}
            </LabelCurrency>
          )}
        </InputWrapper>
      </div>
    );
  }
}

Input.propTypes = {
  type: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  val: PropTypes.string,
  placeholder: PropTypes.string,
  desktop: PropTypes.bool,
  invalid: PropTypes.bool,
  onChange: PropTypes.func,
  onChangeDeb: PropTypes.func
};



// WEBPACK FOOTER //
// ./src/components/Input.js